import { X } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import type { FormEvent } from 'react'
import { useT } from '../i18n/t'
import { subscribeToWaitlist } from '../lib/api/waitlist'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Props = {
  open: boolean
  onDismiss: () => void
  onSubmitted: () => void
}

export function EmailWaitlistModal({ open, onDismiss, onSubmitted }: Props) {
  const { t } = useT()
  const titleId = useId()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!open) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onDismiss, busy])

  useEffect(() => {
    if (!open) return
    setEmail('')
    setBusy(false)
    setError(null)
    setSuccess(false)
  }, [open])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const normalizedEmail = email.trim().toLowerCase()

    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setError(t('landing.waitlistPopup.form.error.invalidEmail'))
      setSuccess(false)
      return
    }

    setBusy(true)
    setError(null)

    try {
      await subscribeToWaitlist({
        email: normalizedEmail,
        source: 'landing-popup',
      })
      setSuccess(true)
      setEmail('')
      window.setTimeout(() => onSubmitted(), 2200)
    } catch (submitError) {
      setSuccess(false)
      setError(
        submitError instanceof Error
          ? submitError.message
          : t('landing.waitlistPopup.form.error.generic'),
      )
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[1250] flex items-end justify-center p-4 sm:items-center"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-ink/45 backdrop-blur-[2px]"
        aria-label={t('landing.waitlistPopup.closeAria')}
        onClick={onDismiss}
        disabled={busy}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md pds-surface bg-paper p-6"
      >
        <button
          type="button"
          onClick={onDismiss}
          disabled={busy}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full text-ink/60 transition hover:bg-ink/[0.06] hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 disabled:opacity-50"
          aria-label={t('landing.waitlistPopup.closeAria')}
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        <h2 id={titleId} className="pr-8 text-xl font-extrabold text-ink md:text-2xl">
          {t('landing.waitlistPopup.title')}
        </h2>
        <p className="mt-2 text-sm font-semibold leading-relaxed text-ink/70">
          {t('landing.waitlistPopup.body')}
        </p>

        {success ? (
          <p className="mt-5 text-sm font-semibold leading-relaxed text-emerald-700">
            {t('landing.waitlistPopup.form.success')}
          </p>
        ) : (
          <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-3">
            <label htmlFor="waitlist-popup-email" className="text-sm font-bold text-ink">
              {t('landing.waitlistPopup.form.label')}
            </label>
            <input
              id="waitlist-popup-email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                if (error) setError(null)
              }}
              placeholder={t('landing.waitlistPopup.form.placeholder')}
              autoComplete="email"
              className="h-11 w-full border border-ink/15 bg-ink/[0.03] px-4 text-[15px] font-medium text-ink outline-none transition placeholder:text-ink/35 focus:border-ink/25 focus:bg-ink/[0.05] focus:ring-1 focus:ring-ink/20 disabled:opacity-50"
              required
              disabled={busy}
            />
            {error ? <p className="text-xs font-medium text-rose-700">{error}</p> : null}
            <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onDismiss}
                disabled={busy}
                className="pds-btn-outline h-11 px-5 text-sm font-extrabold text-ink transition hover:bg-ink/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 disabled:opacity-50"
              >
                {t('landing.waitlistPopup.later')}
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex h-11 items-center justify-center rounded-lg bg-ink px-5 text-sm font-extrabold text-paper transition hover:-translate-y-0.5 hover:bg-ink/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {busy
                  ? t('landing.waitlistPopup.form.submitting')
                  : t('landing.waitlistPopup.form.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
